// src/services/demoService.js
//
// Firestore operations for the demo-day check-out app (DemoFlow).
// SPEC: /docs/SPEC-2026-07-22-demo-checkout.md §5.
//
// Design rule: the beach client performs FIRESTORE WRITES ONLY (offline-
// queueable). All external I/O — waiver PDF, SendGrid, Klaviyo, Storage —
// happens in Cloud Function triggers (functions/demo.js). Signature/photo
// images therefore travel INLINE on the waiver doc (data URLs) and the
// trigger moves them to Storage server-side.

import {
  collection, doc, addDoc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, onSnapshot, serverTimestamp, runTransaction,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../config/firebase';
import { DEMO_COLLECTIONS as C } from '../config/flavor';
import { SEED_RETAILERS } from '../config/demoRetailers';
import { CONSENT_TEXT } from '../config/demoWaiver';

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

export const normalizeEmail = (email) => (email || '').trim().toLowerCase();

export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalizeEmail(email));

// ─────────────────────────────────────────────────────────────────────
// Lead-capture address checks (round 2 §2b)
//
// Riders type their own address into the waiver. Leads are typed BY STAFF, at
// speed, at a tent — and 2 of 4 leads on 2026-08-01 were unreachable through a
// typo that nothing surfaced at the time: `knabski@gmail.vom` (invalid TLD;
// `introEmail.status` still read `sent`) and `lesbaumar@gmail.com` (one letter
// off the address the same person had typed into his own waiver minutes
// earlier). `isValidEmail` above passes both — `.vom` is a structurally valid
// TLD and the second is a valid address belonging to someone else.
//
// These checks WARN, they never block: an unusual but real domain must stay
// enterable. The UI reads the address back to the rider instead of showing the
// staffer a validation error — the rider is standing right there and is the
// only one who actually knows.
// ─────────────────────────────────────────────────────────────────────

// Not exhaustive by design — anything outside it is merely *worth a second
// look*, never rejected. Covers WAKA's realistic customer base plus the common
// gTLDs. Add to it freely; a false warning costs one tap.
const COMMON_TLDS = new Set([
  'com', 'net', 'org', 'edu', 'gov', 'mil', 'int', 'info', 'biz', 'io', 'co', 'me', 'tv', 'cc',
  'us', 'uk', 'ca', 'au', 'nz', 'ie', 'de', 'fr', 'es', 'it', 'nl', 'be', 'ch', 'at', 'se',
  'no', 'dk', 'fi', 'is', 'pt', 'pl', 'cz', 'sk', 'hu', 'ro', 'gr', 'tr', 'ru', 'ua',
  'br', 'mx', 'ar', 'cl', 'pe', 'co.uk', 'com.au', 'co.nz', 'jp', 'cn', 'kr', 'hk', 'sg',
  'in', 'id', 'th', 'ph', 'my', 'vn', 'il', 'ae', 'sa', 'za', 'ke', 'ng', 'eg', 'ma',
  'app', 'dev', 'ai', 'xyz', 'online', 'site', 'store', 'shop', 'club', 'live', 'life',
  'cloud', 'email', 'tech', 'digital', 'studio', 'design', 'agency', 'media', 'works',
  'group', 'team', 'world', 'solutions', 'consulting', 'surf', 'ski', 'fit', 'run', 'bike',
]);

// The TLD looks unusual → worth reading back. Returns the offending TLD, or null.
export const suspiciousTld = (email) => {
  const domain = normalizeEmail(email).split('@')[1] || '';
  if (!domain.includes('.')) return null;
  const parts = domain.split('.');
  const tld = parts[parts.length - 1];
  const twoPart = parts.slice(-2).join('.');
  if (COMMON_TLDS.has(tld) || COMMON_TLDS.has(twoPart)) return null;
  return tld || null;
};

// Levenshtein, short-circuited — we only ever care about "within one edit".
const editDistanceWithin1 = (a, b) => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [s, t] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < s.length && j < t.length) {
    if (s[i] === t[j]) { i += 1; j += 1; continue; }
    edits += 1;
    if (edits > 1) return false;
    if (s.length === t.length) { i += 1; j += 1; } else { j += 1; }
  }
  return edits + (t.length - j) + (s.length - i) <= 1;
};

// Is this address one edit away from someone already captured at this event?
// `known` is the output of listEventContactEmails. Exact matches are NOT
// flagged — the same person legitimately appearing twice is not a typo.
export const nearDuplicateContact = (email, known = []) => {
  const target = normalizeEmail(email);
  if (!target) return null;
  return known.find((k) => k.email && k.email !== target && editDistanceWithin1(k.email, target)) || null;
};

// Every address already captured at this event, riders and leads alike, so a
// mistyped lead can be caught against the waiver the same person just signed.
// A failed lookup returns [] — this check must never block capture.
export async function listEventContactEmails(eventId) {
  if (!eventId) return [];
  const out = [];
  const grab = async (coll, source) => {
    try {
      const snap = await getDocs(query(collection(db, coll), where('eventId', '==', eventId)));
      snap.docs.forEach((d) => {
        const v = d.data();
        if (!v.riderEmail) return;
        out.push({
          email: normalizeEmail(v.riderEmail),
          name: `${v.riderFirstName || ''} ${v.riderLastName || ''}`.trim(),
          source,
        });
      });
    } catch { /* non-fatal by design */ }
  };
  await grab(C.demoWaivers, 'rider');
  await grab(C.demoLeads, 'lead');
  return out;
}

// "Hood River Event Site" → "hood-river-event-site"
export const slugify = (name) =>
  (name || '')
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

// Local calendar date — sessions/waivers are day-scoped at a spot.
export const localDateKey = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Has this demo day's date already passed? The picker only ever lists
// `date >= today`, but the sticky-day restore adopts a stored id whose doc still
// exists — and a day doc from three weeks ago still exists. A phone left on the
// rack over a fortnight would come back scoped to a finished event, so the date
// is re-checked on restore AND at the point of use (round 2 §1).
export const isPastDayDate = (date) => !!date && date < localDateKey();

// ── Multi-day events (Nigel 2026-09-08, AWSI Wed–Sat) ────────────────────
//
// A demo day may span several dates: `date` is the first day, optional
// `endDate` the last. The eventId stays `{spotId}_{date}` — the START date —
// so it is still minted once and still comes only from the day doc. That is
// deliberate and load-bearing: the fleet is keyed `{eventId}_{sku}`, so a
// board scanned onto the rack on Wednesday is still on it on Saturday, and a
// board still checked out at midnight can be checked back in the next
// morning. Nothing about the 2026-08-01 invariant changes — we only widen the
// window in which a given eventId is considered live.
//
// `endDate` absent/null = single-day, exactly as before.
export const dayLastDate = (day) => (day && (day.endDate || day.date)) || null;

// Is this day over? Multi-day events stay live until their LAST date. Takes
// the day OBJECT (not a bare date) — a bare date can't know about endDate.
export const isPastDay = (day) => {
  const last = dayLastDate(day);
  return !!last && last < localDateKey();
};

// "2026-08-01" → "Sat 1 Aug 2026". Noon avoids the UTC-parse date shift.
// Shared by the scan-home header and the day picker so the date a staffer
// confirms is rendered identically to the one they then work under.
export const formatDayDate = (date) => {
  if (!date) return null;
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

// A day's date, or its span when it runs over several dates:
// "Wed 9 Sep – Sat 12 Sep 2026". Single-day events read exactly as before.
export const formatDayRange = (day) => {
  if (!day || !day.date) return null;
  const last = dayLastDate(day);
  if (!last || last === day.date) return formatDayDate(day.date);
  const d = new Date(`${day.date}T12:00:00`);
  const start = Number.isNaN(d.getTime())
    ? day.date
    : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${start} – ${formatDayDate(last)}`;
};

// How many dates the event covers (1 = ordinary single-day demo day).
export const dayCount = (day) => {
  const last = dayLastDate(day);
  if (!day || !day.date || !last || last === day.date) return 1;
  const a = new Date(`${day.date}T12:00:00`);
  const b = new Date(`${last}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 1;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
};

// Shape of an eventId, for reference and for createDemoDay/updateDemoDay which
// MINT one alongside the demoDays doc.
//
// ⚠ Do NOT use this to derive the scope the app is operating in. The running
// app takes its eventId from the demoDays doc id it selected (DemoFlow's
// `demoDay.id`) — never by combining a remembered spot with today's date. That
// synthesis is what produced the 2026-08-01 split fleet; see getDefaultDayId.
export const eventIdFor = (spotId, date = new Date()) => `${spotId}_${localDateKey(date)}`;

// Extract a SKU from a scanned QR payload. Board QRs encode
// https://my.waka.surf/r/{SKU} (historically register.waka.surf); also accept
// a bare SKU typed/encoded directly.
export function skuFromScan(payload) {
  if (!payload) return null;
  const raw = String(payload).trim();
  const urlMatch = raw.match(/\/r\/([A-Za-z0-9-]+)/);
  const candidate = (urlMatch ? urlMatch[1] : raw).toUpperCase();
  // PRODUCT-SIZE-VERSION-SERIAL — same shape checkRegistration accepts.
  return /^[A-Z]+-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+$/.test(candidate) ? candidate : null;
}

// ─────────────────────────────────────────────────────────────────────
// Staff + approval flow (SPEC §3.1)
// ─────────────────────────────────────────────────────────────────────

// Resolve the signed-in user's demo role.
// Returns { role: 'admin' | 'waka' | 'partner' | null, staffDoc }.
export async function getStaffRole(user) {
  if (!user?.email) return { role: null, staffDoc: null };
  const email = normalizeEmail(user.email);

  const adminSnap = await getDoc(doc(db, C.admins, email));
  if (adminSnap.exists()) return { role: 'admin', staffDoc: null };

  // Any @waka.surf Google account is WAKA demo staff automatically (Nigel,
  // 2026-07-22). Mirrored server-side in the Firestore rules (isWakaSurfEmail).
  if (email.endsWith('@waka.surf')) return { role: 'waka', staffDoc: null };

  const staffSnap = await getDoc(doc(db, C.demoStaff, email));
  if (staffSnap.exists() && staffSnap.data().active === true) {
    return { role: staffSnap.data().role === 'waka' ? 'waka' : 'partner', staffDoc: staffSnap.data() };
  }
  return { role: null, staffDoc: staffSnap.exists() ? staffSnap.data() : null };
}

export async function submitStaffRequest(user, { name, affiliation }) {
  const email = normalizeEmail(user.email);
  await setDoc(doc(db, C.demoStaffRequests, email), {
    email,
    name: (name || '').trim(),
    affiliation: (affiliation || '').trim(),
    status: 'pending',
    requestedAt: serverTimestamp(),
    uid: user.uid,
  });
}

export function watchOwnStaffRequest(user, cb) {
  const email = normalizeEmail(user.email);
  return onSnapshot(doc(db, C.demoStaffRequests, email), (snap) =>
    cb(snap.exists() ? snap.data() : null));
}

export function watchPendingStaffRequests(cb) {
  const q = query(collection(db, C.demoStaffRequests), where('status', '==', 'pending'));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => cb([])); // permission-denied for partner staff → empty
}

export async function decideStaffRequest(request, approve, decidedByUser) {
  const email = normalizeEmail(request.email || request.id);
  await updateDoc(doc(db, C.demoStaffRequests, email), {
    status: approve ? 'approved' : 'declined',
    decidedBy: decidedByUser.uid,
    decidedByEmail: normalizeEmail(decidedByUser.email),
    decidedAt: serverTimestamp(),
  });
  if (approve) {
    await setDoc(doc(db, C.demoStaff, email), {
      role: 'partner',
      partnerName: request.affiliation || null,
      active: true,
      addedBy: 'request-approval',
      approvedBy: decidedByUser.uid,
      approvedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });
  }
}

// ─────────────────────────────────────────────────────────────────────
// Demo spots (SPEC §4.0 / §5.1) — set-once sticky default
// ─────────────────────────────────────────────────────────────────────

const SPOT_LS_KEY = 'waka-demo-spot';
const DAY_LS_KEY = 'waka-demo-day';

export const getDefaultSpotId = () => {
  try { return localStorage.getItem(SPOT_LS_KEY) || null; } catch { return null; }
};

export const setDefaultSpotId = (spotId) => {
  try { localStorage.setItem(SPOT_LS_KEY, spotId); } catch { /* private mode */ }
};

// Sticky demo DAY (2026-08-01 split-fleet incident). Persisting the spot alone
// was the bug: `demoDate` lived in memory and defaulted to today, so a device
// that restored the sticky spot instead of going through the picker silently
// scoped itself to {stickySpot}_{today} — an eventId nobody created. Two staff
// then ran two parallel fleets all day. The DAY id is the whole eventId, so
// restoring it restores the scope exactly, with no date arithmetic.
export const getDefaultDayId = () => {
  try { return localStorage.getItem(DAY_LS_KEY) || null; } catch { return null; }
};

export const setDefaultDayId = (dayId) => {
  try { localStorage.setItem(DAY_LS_KEY, dayId); } catch { /* private mode */ }
};

export const clearDefaultDayId = () => {
  try { localStorage.removeItem(DAY_LS_KEY); } catch { /* private mode */ }
};

export async function getSpot(spotId) {
  const snap = await getDoc(doc(db, C.demoSpots, spotId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function listRecentSpots(max = 5) {
  try {
    const q = query(collection(db, C.demoSpots), orderBy('lastUsedAt', 'desc'), limit(max));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}

// Create-or-select a spot by name; stamps lastUsedAt and sets the sticky default.
export async function selectSpot(name, user) {
  const spotId = slugify(name);
  if (!spotId) throw new Error('Please enter a location name.');
  const ref = doc(db, C.demoSpots, spotId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    await updateDoc(ref, { lastUsedAt: serverTimestamp() });
  } else {
    await setDoc(ref, {
      name: name.trim(),
      active: true,
      overdueMinutes: 60,                       // SPEC §11 default
      waiverVersion: null,                      // null → app uses the built-in pinned version
      photoCapture: 'off',                      // 'off' | 'face' | 'id' (SPEC §11 default)
      klaviyoListId: null,                      // null → Cloud Function skips Klaviyo
      staffEmails: [],
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      lastUsedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  setDefaultSpotId(spotId);
  return getSpot(spotId);
}

// ─────────────────────────────────────────────────────────────────────
// Fleet (SPEC §5.2)
// ─────────────────────────────────────────────────────────────────────

const fleetDocId = (eventId, sku) => `${eventId}_${sku}`;

export async function getFleetBoard(eventId, sku) {
  const snap = await getDoc(doc(db, C.demoFleet, fleetDocId(eventId, sku)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Is this serial someone's PERSONAL registered board? Reads the world-readable
// publicRegistrations/{sku} doc (always the PRODUCTION collection — ownership
// truth lives in prod regardless of demo flavor). Returns { ownerName } or null.
// Used to gate add-to-fleet behind an explicit "add as loaner" confirm so a
// staff scan can never silently absorb Nigel's or an ambassador's own board.
export async function getRegisteredOwner(sku) {
  try {
    const snap = await getDoc(doc(db, 'publicRegistrations', (sku || '').toUpperCase()));
    if (!snap.exists()) return null;
    const d = snap.data();
    return { ownerName: d.registeredUserName || 'a WAKA owner' };
  } catch { return null; }
}

export async function addBoardToFleet(eventId, product, user, { loaner = false, ownerName = null } = {}) {
  const ref = doc(db, C.demoFleet, fleetDocId(eventId, product.sku));
  await setDoc(ref, {
    eventId,
    sku: product.sku,
    productName: product.productName,
    size: product.size,
    // Loaner = a personally-registered board temporarily in the demo fleet
    // (Nigel's, an ambassador's). Ownership is NEVER touched — this flag is
    // display + a server-side guard against auto-registration.
    loaner,
    loanerOwnerName: ownerName,
    status: 'available',
    currentSessionId: null,
    maintenanceNote: null,
    addedBy: user.uid,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return getFleetBoard(eventId, product.sku);
}

export function watchFleet(eventId, cb) {
  const q = query(collection(db, C.demoFleet), where('eventId', '==', eventId));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

export async function clearMaintenance(eventId, sku) {
  await updateDoc(doc(db, C.demoFleet, fleetDocId(eventId, sku)), {
    status: 'available', maintenanceNote: null, updatedAt: serverTimestamp(),
  });
}

// ─────────────────────────────────────────────────────────────────────
// Waivers (SPEC §5.4)
// ─────────────────────────────────────────────────────────────────────

// Repeat-rider lookup: a signed waiver for this event + waiver version.
export async function findWaiverOnFile(eventId, riderEmail, waiverVersion) {
  const q = query(
    collection(db, C.demoWaivers),
    where('eventId', '==', eventId),
    where('riderEmail', '==', normalizeEmail(riderEmail)),
    where('waiverVersion', '==', waiverVersion),
    limit(1),
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function createWaiver(eventId, spot, rider, signing, user) {
  const docData = {
    eventId,
    spotName: spot.name,
    riderFirstName: rider.firstName.trim(),
    riderLastName: rider.lastName.trim(),
    riderEmail: normalizeEmail(rider.email),
    riderPhone: rider.phone ? rider.phone.trim() : null,
    isMinor: signing.isMinor === true,
    guardianName: signing.guardianName || null,
    guardianRelationship: signing.guardianRelationship || null,
    waiverVersion: signing.waiverVersion,
    waiverTextHash: signing.waiverTextHash,
    typedLegalName: signing.typedLegalName.trim(),
    // Inline images — the onDemoWaiverCreated trigger moves these to Storage,
    // writes signaturePath/photoPath/pdfPath, and strips the inline fields.
    signatureDataUrl: signing.signatureDataUrl,
    photoDataUrl: signing.photoDataUrl || null,
    photoType: signing.photoType || null,
    photoDeclined: signing.photoDeclined === true,
    signaturePath: null,
    photoPath: null,
    pdfPath: null,
    emailConsent: signing.emailConsent === true,
    consentText: signing.consentText,
    signedAt: serverTimestamp(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    capturedBy: user.uid,
    capturedByEmail: normalizeEmail(user.email),
    klaviyoSync: { status: 'pending', syncedAt: null, error: null },
  };
  const ref = await addDoc(collection(db, C.demoWaivers), docData);
  return { id: ref.id, ...docData };
}

// ─────────────────────────────────────────────────────────────────────
// Sessions (SPEC §5.3) — check-out / check-in / void
// ─────────────────────────────────────────────────────────────────────

// State transitions run in a TRANSACTION over the fleet doc + session doc
// (2026-08-01). Two properties matter, and neither is optional hardening:
//
//  1. checkOut used setDoc(…, { merge: true }) on the fleet doc, which CREATES
//     it when absent. A device on a bogus eventId therefore never errored — it
//     materialised a parallel fleet doc and looked perfectly healthy. That is
//     the whole reason a scoping bug presented as a mystery for a full day.
//     The fleet doc must now already exist; addBoardToFleet is the only
//     creation path, as was always intended.
//  2. Asserting the starting status closes the two-device double-checkout race.
//
// Transactions also require connectivity, so an offline attempt fails visibly
// instead of queueing into a divergent local mirror. Errors here are written to
// be shown to staff verbatim — DemoConfirm / DemoCheckIn surface `.message`.

// Thrown when a transition's precondition fails. Carries a staff-legible
// message; `code` lets callers branch without matching on prose.
export class DemoStateError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'DemoStateError';
    this.code = code;
  }
}

const readFleetForTransition = async (tx, eventId, sku) => {
  const ref = doc(db, C.demoFleet, fleetDocId(eventId, sku));
  const snap = await tx.get(ref);
  if (!snap.exists()) {
    throw new DemoStateError(
      'fleet-missing',
      "This board isn't on today's demo day. Go back, scan it again and add it to the fleet first — "
      + "if it's already been added, check the day shown at the top of the scan screen matches the "
      + 'other staff phone.',
    );
  }
  return { ref, data: snap.data() };
};

export async function checkOut(eventId, product, rider, waiverId, emailConsent, user) {
  const session = {
    eventId,
    sku: product.sku,
    productName: product.productName,
    size: product.size,
    riderFirstName: rider.firstName.trim(),
    riderLastName: rider.lastName.trim(),
    riderEmail: normalizeEmail(rider.email),
    riderPhone: rider.phone ? rider.phone.trim() : null,
    waiverId,
    emailConsent: emailConsent === true,
    status: 'out',
    checkedOutAt: serverTimestamp(),
    checkedOutBy: user.uid,
    checkedOutByEmail: normalizeEmail(user.email),
    checkedInAt: null,
    checkedInBy: null,
    condition: null,
    conditionNote: null,
    voidReason: null,
    rideEmail: null,
  };
  // Session id minted client-side so both writes land in one transaction.
  const sessionRef = doc(collection(db, C.demoSessions));
  await runTransaction(db, async (tx) => {
    const fleet = await readFleetForTransition(tx, eventId, product.sku);
    if (fleet.data.status === 'out') {
      throw new DemoStateError(
        'already-out',
        `${product.productName || product.sku} is already checked out — another phone may have `
        + 'just taken it. Go back and scan it again to see who has it.',
      );
    }
    if (fleet.data.status === 'maintenance') {
      throw new DemoStateError(
        'in-maintenance',
        `${product.productName || product.sku} is benched for maintenance. `
        + 'Clear it from the board card before checking it out.',
      );
    }
    if (fleet.data.status !== 'available') {
      throw new DemoStateError(
        'unexpected-status',
        `${product.productName || product.sku} isn't available to check out (status: ${fleet.data.status}).`,
      );
    }
    tx.set(sessionRef, session);
    tx.update(fleet.ref, {
      status: 'out',
      currentSessionId: sessionRef.id,
      maintenanceNote: null,
      updatedAt: serverTimestamp(),
    });
  });
  return { id: sessionRef.id, ...session };
}

export async function getSession(sessionId) {
  const snap = await getDoc(doc(db, C.demoSessions, sessionId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Shared precondition for the two "board comes back" transitions: the session
// must still be open. A session already returned/voided means another phone got
// there first — say so rather than writing over their record.
const readOpenSessionForTransition = async (tx, sessionId) => {
  const ref = doc(db, C.demoSessions, sessionId);
  const snap = await tx.get(ref);
  if (!snap.exists()) {
    throw new DemoStateError('session-missing', 'That session no longer exists. Scan the board again.');
  }
  const data = snap.data();
  if (data.status !== 'out') {
    throw new DemoStateError(
      'not-open',
      data.status === 'returned'
        ? 'This board has already been checked in — another phone got there first.'
        : `This session is ${data.status}, so it can't be closed again.`,
    );
  }
  return { ref, data };
};

export async function checkIn(session, { condition = 'ok', conditionNote = null, checkInNote = null }, user) {
  const damaged = condition === 'damage';
  await runTransaction(db, async (tx) => {
    // Both reads before any write — Firestore transaction requirement.
    const open = await readOpenSessionForTransition(tx, session.id);
    const fleet = await readFleetForTransition(tx, session.eventId, session.sku);
    tx.update(open.ref, {
      status: 'returned',
      checkedInAt: serverTimestamp(),
      checkedInBy: user.uid,
      checkedInByEmail: normalizeEmail(user.email),
      condition,
      conditionNote,
      // General check-in notes (Nigel 2026-07-22) — optional, independent of the
      // damage flag: rider feedback, fin swaps, anything worth remembering.
      checkInNote: checkInNote ? checkInNote.trim() : null,
    });
    tx.update(fleet.ref, {
      status: damaged ? 'maintenance' : 'available',
      maintenanceNote: damaged ? (conditionNote || 'Damage flagged at check-in') : null,
      currentSessionId: null,
      updatedAt: serverTimestamp(),
    });
  });
}

export async function voidSession(session, reason, user) {
  await runTransaction(db, async (tx) => {
    const open = await readOpenSessionForTransition(tx, session.id);
    const fleet = await readFleetForTransition(tx, session.eventId, session.sku);
    tx.update(open.ref, {
      status: 'voided',
      voidReason: reason || 'voided by staff',
      voidedBy: user.uid,
      voidedAt: serverTimestamp(),
    });
    tx.update(fleet.ref, {
      status: 'available',
      currentSessionId: null,
      updatedAt: serverTimestamp(),
    });
  });
}

export function watchOpenSessions(eventId, cb) {
  const q = query(
    collection(db, C.demoSessions),
    where('eventId', '==', eventId),
    where('status', '==', 'out'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

export function watchDaySessions(eventId, cb) {
  const q = query(collection(db, C.demoSessions), where('eventId', '==', eventId));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

export const currentUser = () => auth.currentUser;

// ─────────────────────────────────────────────────────────────────────
// Reports (Nigel 2026-07-22) — email the requesting staff member a demo-day
// report (riders, in/out times, durations, condition + notes, consent).
// Server-side callable does the queries + SendGrid send; reportType exists so
// future reports slot in without new plumbing.
// ─────────────────────────────────────────────────────────────────────

export async function emailDemoDayReport(eventId) {
  const fn = httpsCallable(functions, 'sendDemoDayReport');
  const flavor = C.demoSessions.endsWith('_demo_preview') ? 'demo_preview' : 'demo';
  return (await fn({ eventId, flavor, reportType: 'demo-day' })).data;
}

// ─────────────────────────────────────────────────────────────────────
// Season demos (Nigel 2026-09-06) — an ambassador running a spot all winter.
//
// demoSpots/{spotId}.season = { active, ambassadorName, discountCode,
// referralCode, staffEmails[] }. Admin/WAKA set it once (rules block partner
// staff from writing `season`). A listed staffer then taps "Open today at
// <spot>" and the openDemoDayAtSpot callable creates demoDays/{spotId}_{today}
// with offers copied from the spot — so the day doc stays the only source of
// eventId (2026-08-02 invariant) and demoDays rules stay admin-only.
// ─────────────────────────────────────────────────────────────────────

// Spots this user can open a day at. WAKA/admin: every active season spot.
// Partner: only spots whose season.staffEmails lists them.
export async function listSeasonSpotsFor(user, role) {
  const email = normalizeEmail(user?.email);
  if (!email) return [];
  try {
    const snap = await getDocs(query(collection(db, C.demoSpots), where('season.active', '==', true)));
    const spots = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (role === 'admin' || role === 'waka') return spots;
    return spots.filter((s) => ((s.season || {}).staffEmails || []).map(normalizeEmail).includes(email));
  } catch { return []; }
}

export async function openTodayAtSpot(spotId) {
  const fn = httpsCallable(functions, 'openDemoDayAtSpot');
  const flavor = C.demoSessions.endsWith('_demo_preview') ? 'demo_preview' : 'demo';
  const { data } = await fn({ spotId, date: localDateKey(), flavor });
  return data; // { id, spotId, spotName, date, offers, created }
}

// Admin/WAKA only (rules). Creates the spot if needed, then writes `season`.
export async function saveSpotSeason(name, season, user) {
  const spot = await selectSpot(name, user);
  const clean = {
    active: season.active === true,
    ambassadorName: (season.ambassadorName || '').trim() || null,
    discountCode: (season.discountCode || '').trim().toUpperCase() || null,
    referralCode: (season.referralCode || '').trim().toUpperCase() || null,
    staffEmails: (season.staffEmails || []).map(normalizeEmail).filter(isValidEmail),
  };
  await updateDoc(doc(db, C.demoSpots, spot.id), {
    season: clean,
    updatedAt: serverTimestamp(),
    updatedBy: normalizeEmail(user.email),
  });
  return { ...spot, season: clean };
}

export async function listSeasonSpots() {
  try {
    const snap = await getDocs(collection(db, C.demoSpots));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.season);
  } catch { return []; }
}

// ─────────────────────────────────────────────────────────────────────
// Demo DAYS + retailer offers (Nigel 2026-07-22)
//
// A demo DAY is an admin-created event: spot + date (+ optional start time)
// + offers. Offers drive the ride-summary email's CTA per rider:
//   - a partner retailer working the event (e.g. Windance) lists the sizes
//     they stock + their discount code → riders of those sizes get the
//     retailer's board URL + code;
//   - everyone else gets waka.surf (+ the default WAKA code, if set).
// The retailer catalogue (demoRetailers/, shared across flavors) holds each
// dealer's per-size WAKA board URLs — admin-maintained, dropdown-selectable.
// ONLY admins create/edit demo days and the catalogue (rules-enforced).
// ─────────────────────────────────────────────────────────────────────

const DEMO_DAYS = C.demoSpots.endsWith('_demo_preview') ? 'demoDays_demo_preview' : 'demoDays';
const DEMO_RETAILERS = 'demoRetailers'; // shared across flavors, like demoStaff

// Soonest first: date, then start time (Nigel 2026-07-23).
const byDateTime = (a, b) => {
  const ka = `${a.date || ''}T${a.startTime || '00:00'}`;
  const kb = `${b.date || ''}T${b.startTime || '00:00'}`;
  return ka < kb ? -1 : ka > kb ? 1 : 0;
};

export async function listUpcomingDemoDays() {
  // Small collection — fetch and filter client-side (avoids composite indexes).
  const snap = await getDocs(collection(db, DEMO_DAYS));
  const today = localDateKey();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => dayLastDate(d) >= today)
    .sort(byDateTime);
}

// LIVE picker feed (Nigel 2026-07-23 duplicate-day incident): a stale fetched
// list let an admin edit a day that had already been renamed on the server,
// which resurrected the deleted doc. onSnapshot keeps every device's list
// honest the moment a day is created, moved, or deleted.
export function watchUpcomingDemoDays(cb) {
  return onSnapshot(collection(db, DEMO_DAYS), (snap) => {
    const today = localDateKey();
    cb(snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((d) => dayLastDate(d) >= today)
      .sort(byDateTime));
  });
}

export async function getDemoDay(eventId) {
  const snap = await getDoc(doc(db, DEMO_DAYS, eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Admin-only (rules enforce). Creates/updates the spot doc too so per-spot
// settings (overdueMinutes, photoCapture, klaviyoListId) keep working.
export async function createDemoDay({ name, date, endDate, startTime, endTime, offers }, user) {
  const spot = await selectSpot(name, user);
  // eventId is minted from the START date only — see dayLastDate. A multi-day
  // event keeps ONE eventId for its whole run, which is what carries the fleet
  // (and any board still checked out) across midnight.
  const eventId = `${spot.id}_${date}`;
  await setDoc(doc(db, DEMO_DAYS, eventId), {
    spotId: spot.id,
    spotName: spot.name,
    date,
    endDate: endDate && endDate > date ? endDate : null,
    startTime: startTime || null,
    endTime: endTime || null,
    offers: offers || { default: { discountCode: null }, retailers: [] },
    createdBy: user.uid,
    createdByEmail: normalizeEmail(user.email),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: eventId, spot };
}

// Admin-only. Edits an existing demo day (Nigel 2026-07-23). Times, codes and
// offers change in place. If LOCATION or DATE change, the eventId changes too
// — and sessions/waivers/fleet docs are all keyed by eventId — so migration is
// only allowed while the day is untouched; otherwise we refuse with a clear
// message rather than orphaning the day's records.
export async function updateDemoDay(originalId, { name, date, endDate, startTime, endTime, offers }, user) {
  // Guard FIRST (2026-07-23 duplicate-day incident): if the day being edited
  // was renamed/deleted meanwhile (another device, or an earlier date-change
  // save from a stale list), a blind setDoc would RESURRECT the old doc as a
  // duplicate event. Refuse and make the admin reopen the fresh list.
  const origSnap = await getDoc(doc(db, DEMO_DAYS, originalId));
  if (!origSnap.exists()) {
    throw new Error(
      'This demo day was moved or deleted since the list loaded — go back and reopen it from the list.',
    );
  }
  const orig = origSnap.data();

  const spot = await selectSpot(name, user);
  const eventId = `${spot.id}_${date}`;

  if (eventId !== originalId) {
    const inUse = async (coll) => !(await getDocs(
      query(collection(db, coll), where('eventId', '==', originalId), limit(1)),
    )).empty;
    if (await inUse(C.demoSessions) || await inUse(C.demoWaivers) || await inUse(C.demoFleet)) {
      throw new Error(
        'This demo day already has boards or riders on it — location and date are locked. '
        + 'Adjust times, codes and offers here, or create a separate demo day.',
      );
    }
  }

  await setDoc(doc(db, DEMO_DAYS, eventId), {
    spotId: spot.id,
    spotName: spot.name,
    date,
    // Extending or shortening the run does NOT change the eventId, so an
    // admin can push the end date out mid-event without orphaning the fleet.
    endDate: endDate && endDate > date ? endDate : null,
    startTime: startTime || null,
    endTime: endTime || null,
    offers: offers || { default: { discountCode: null }, retailers: [] },
    createdBy: orig.createdBy || user.uid,
    createdByEmail: orig.createdByEmail || normalizeEmail(user.email),
    createdAt: orig.createdAt || serverTimestamp(),
    updatedBy: normalizeEmail(user.email),
    updatedAt: serverTimestamp(),
  });
  if (eventId !== originalId) await deleteDoc(doc(db, DEMO_DAYS, originalId));
  return { id: eventId, spot };
}

export async function listRetailers() {
  const snap = await getDocs(collection(db, DEMO_RETAILERS));
  const saved = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // Seed the dropdown with the web store's known retailer list (see
  // config/demoRetailers.js — sourced from waka-theme's PDP out-of-stock
  // panel). A saved catalogue doc with the same id always wins, so admin
  // edits (including active: false to hide a seed) stick.
  const savedIds = new Set(saved.map((r) => r.id));
  const merged = [...saved, ...SEED_RETAILERS.filter((r) => !savedIds.has(r.id))];
  return merged
    .filter((r) => r.active !== false)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// ─────────────────────────────────────────────────────────────────────
// Lead capture (Nigel 2026-07-23): walk-ups at the tent who don't demo a
// board. Firestore-write-only like everything else — onDemoLeadCreated (CF)
// sends the intro email and syncs Klaviyo (consent-gated). Field names match
// the waiver doc (riderEmail/riderFirstName/…) so the server-side Klaviyo
// helper works unchanged for both.
// ─────────────────────────────────────────────────────────────────────

export async function createLead(eventId, spot, { firstName, lastName, email, emailConsent }, user) {
  const ref = await addDoc(collection(db, C.demoLeads), {
    eventId,
    spotId: spot.id,
    spotName: spot.name,
    riderFirstName: firstName.trim(),
    riderLastName: lastName.trim(),
    riderEmail: normalizeEmail(email),
    emailConsent: emailConsent === true,
    consentText: emailConsent === true ? CONSENT_TEXT : null,
    source: 'demo-day-lead',
    capturedBy: user.uid,
    capturedByEmail: normalizeEmail(user.email),
    capturedByName: user.displayName || (user.email || '').split('@')[0],
    createdAt: serverTimestamp(),
    introEmail: { status: 'pending' },
  });
  return ref.id;
}

// Admin-only delete (Nigel 2026-07-23). Same lock as date changes: once a
// day has fleet boards, sessions, or waivers, it's history — keep it for
// reporting instead of deleting.
export async function deleteDemoDay(eventId) {
  const inUse = async (coll) => !(await getDocs(
    query(collection(db, coll), where('eventId', '==', eventId), limit(1)),
  )).empty;
  if (await inUse(C.demoSessions) || await inUse(C.demoWaivers) || await inUse(C.demoFleet)) {
    throw new Error(
      "This demo day already has boards or riders on it, so it can't be deleted — the history feeds reports.",
    );
  }
  await deleteDoc(doc(db, DEMO_DAYS, eventId));
}

// Admin-only. boardUrlsBySize: { '77': 'https://…', '88': 'https://…', … }
export async function saveRetailer({ id, name, boardUrlsBySize }, user) {
  const retailerId = id || slugify(name);
  await setDoc(doc(db, DEMO_RETAILERS, retailerId), {
    name: name.trim(),
    boardUrlsBySize: boardUrlsBySize || {},
    active: true,
    updatedBy: normalizeEmail(user.email),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return retailerId;
}
